import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

// The customer dashboard's layer stack (CB-17, VIS §7.2; design system v2 §5 "Layer
// order: toast → sheet/modal → drawer → page. Esc and browser Back unwind exactly one
// layer"). One module-level stack for the surface — every overlay registers here via
// `useLayer`, so:
//   - Esc closes the TOP layer only;
//   - browser Back closes the top layer only (each open layer pushes one history
//     entry; closing it any other way pops that entry again);
//   - the page behind is scroll-locked while any layer is open;
//   - focus returns to whatever was focused when the layer opened;
//   - z-index follows open order within the design's bands (drawer 60, sheet/scrim 80;
//     toasts sit above at 90 and are not part of the stack — they never take Esc/Back).

export type LayerKind = 'sheet' | 'drawer';

const Z_BASE: Record<LayerKind, number> = { drawer: 60, sheet: 80 };

interface Entry {
  id: number;
  kind: LayerKind;
  close: () => void;
  wantsHistory: boolean;
  pushed: boolean;
  pushTimer: ReturnType<typeof setTimeout> | null;
  returnFocus: Element | null;
}

const stack: Entry[] = [];
let nextId = 1;
let pendingBacks = 0;
let savedOverflow: string | null = null;

function top(): Entry | undefined {
  return stack[stack.length - 1];
}

// DC123: Tab / Shift+Tab stay inside the topmost open modal (`aria-modal="true"`, last
// in document order) while any layer is open. Found from the DOM rather than a ref so
// every existing overlay gets it without an API change.
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function trapTab(e: KeyboardEvent) {
  if (!top()) return;
  const modals = document.querySelectorAll<HTMLElement>('[aria-modal="true"]');
  const root = modals[modals.length - 1];
  if (!root) return;
  const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest('[hidden],[aria-hidden="true"]') && el.getClientRects().length > 0,
  );
  if (items.length === 0) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  const inside = active instanceof Node && root.contains(active);
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Tab') {
    trapTab(e);
    return;
  }
  if (e.key !== 'Escape' || e.defaultPrevented) return;
  const t = top();
  if (!t) return;
  e.preventDefault();
  t.close();
}

function onPopState() {
  // A Back we issued ourselves to drop the entry of a layer closed another way.
  if (pendingBacks > 0) {
    pendingBacks -= 1;
    return;
  }
  const t = top();
  if (!t || !t.pushed) return;
  const state = history.state as { bdLayer?: number } | null;
  if (state?.bdLayer === t.id) return;
  t.pushed = false; // the browser already removed this layer's entry
  t.close();
}

// The popstate listener stays attached once installed: the Back we issue when the last
// layer closes arrives after the stack is empty and must still be consumed here.
let popstateInstalled = false;

function attachGlobals() {
  document.addEventListener('keydown', onKeyDown);
  if (!popstateInstalled) {
    window.addEventListener('popstate', onPopState);
    popstateInstalled = true;
  }
  savedOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
}

function detachGlobals() {
  document.removeEventListener('keydown', onKeyDown);
  document.body.style.overflow = savedOverflow ?? '';
  savedOverflow = null;
}

function register(kind: LayerKind, close: () => void, wantsHistory: boolean): Entry {
  const entry: Entry = {
    id: nextId++,
    kind,
    close,
    wantsHistory,
    pushed: false,
    pushTimer: null,
    returnFocus: typeof document !== 'undefined' ? document.activeElement : null,
  };
  stack.push(entry);
  if (stack.length === 1) attachGlobals();
  if (wantsHistory) {
    // Deferred one task so a StrictMode mount → unmount → mount does not push twice.
    entry.pushTimer = setTimeout(() => {
      entry.pushTimer = null;
      if (!stack.includes(entry)) return;
      const base = (history.state as Record<string, unknown> | null) ?? {};
      history.pushState({ ...base, bdLayer: entry.id }, '');
      entry.pushed = true;
    }, 0);
  }
  return entry;
}

function unregister(entry: Entry) {
  const i = stack.indexOf(entry);
  if (i === -1) return;
  stack.splice(i, 1);
  if (entry.pushTimer) clearTimeout(entry.pushTimer);
  const state = history.state as { bdLayer?: number } | null;
  if (entry.pushed && state?.bdLayer === entry.id) {
    pendingBacks += 1;
    history.back();
  }
  entry.pushed = false;
  if (stack.length === 0) detachGlobals();
  const back = entry.returnFocus;
  if (back instanceof HTMLElement && back.isConnected) back.focus();
}

/** Test/diagnostic view of the stack: open layer ids, bottom → top. */
export function openLayerIds(): number[] {
  return stack.map((e) => e.id);
}

/**
 * Register an overlay while `open`. Returns its z-index (by kind band + open order)
 * and whether it is currently the top layer. `onClose` is called for Esc, Back and
 * scrim clicks — the owner decides what closing means.
 */
export function useLayer(
  open: boolean,
  onClose: () => void,
  { kind = 'sheet', history: wantsHistory = true }: { kind?: LayerKind; history?: boolean } = {},
): { z: number; id: number | null } {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [info, setInfo] = useState<{ z: number; id: number | null }>({ z: Z_BASE[kind], id: null });

  useEffect(() => {
    if (!open) return;
    const entry = register(kind, () => closeRef.current(), wantsHistory);
    setInfo({ z: Z_BASE[kind] + (stack.length - 1) * 2, id: entry.id });
    return () => unregister(entry);
  }, [open, kind, wantsHistory]);

  return info;
}

/** The dimmed backdrop behind a layer (VIS §2.2: rgba(8,16,24,.44), `scrimin`). */
export function LayerScrim({
  z,
  onClose,
  align = 'end',
  className = 'layer-scrim',
  style,
  children,
}: {
  z: number;
  onClose: () => void;
  align?: 'end' | 'center';
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      className={className}
      data-motion=""
      data-align={align}
      style={{ zIndex: z, ...style }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>
  );
}
